import React, { useState, useMemo } from 'react';
import { HiChevronDown, HiCollection } from 'react-icons/hi';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

const StatisticsTab = ({ data }) => {
    const [viewMode, setViewMode] = useState('TABELA');

    // Extract statistics data from backend (format: Estatisticas.estList)
    const statisticsData = useMemo(() => {
        if (!data || !data.Estatisticas) {
            return [];
        }

        // mudar para obj
        if (data.Estatisticas.estList && typeof data.Estatisticas.estList === 'object') { 
            // Converter objeto em array e filtrar apenas com dados
            return Object.values(data.Estatisticas.estList).filter(item => item.Contagem > 0);
        }

        return [];
    }, [data]);

    // Separate TRL and CRL statistics
    const trlStatistics = useMemo(() => {
        return statisticsData.filter(item => item.Coluna && item.Coluna.includes('TRL'));
    }, [statisticsData]);

    const crlStatistics = useMemo(() => {
        return statisticsData.filter(item => item.Coluna && item.Coluna.includes('CRL'));
    }, [statisticsData]);

    const formatNumber = (value) => {
        if (value === null || value === undefined || isNaN(value)) return '-';
        if (typeof value !== 'number') return value;

        // Usar notação científica para valores muito pequenos (mas não zero)
        if (Math.abs(value) < 0.01 && value !== 0) {
            return value.toExponential(2);
        }

        return value.toFixed(2);
    };

    if (!data || Object.keys(data).length === 0) {
        return (
            <div className="p-6 text-center text-gray-500">
                <p>Nenhum dado estatístico disponível</p>
            </div>
        );
    }

    const StatisticsTable = ({ statistics, title }) => {
        if (!statistics || statistics.length === 0) {
            return (
                <div className="bg-white border border-gray-200 rounded-lg p-6">
                    <h3 className="text-lg font-medium text-gray-900 mb-4">{title}</h3>
                    <div className="text-center text-gray-500 py-12">
                        <HiCollection className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                        <p>Nenhum dado disponível</p>
                    </div>
                </div>
            );
        }

        return (
            <div className="overflow-x-auto">
                <table className="divide-y divide-gray-200" style={{ width: 'max-content', minWidth: '100%', outline: 'none' }}>
                    <thead className="bg-gray-50">
                        <tr style={{ outline: 'none' }}>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Transição</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Contagem (projeto)</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Média (dia)</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Mínimo (dia)</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Máximo (dia)</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Desvio Padrão (dia)</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Coef. Variação (%)</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">P25 (dia)</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">P50 (dia)</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">P75 (dia)</th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {statistics.map((stat, index) => (
                                <tr key={index} style={{ outline: 'none' }}>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                        {stat.Coluna}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                        {stat.Contagem}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                        {formatNumber(stat.Média)}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                        {formatNumber(stat.Mínimo)}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                        {formatNumber(stat.Máximo)}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                        {formatNumber(stat['Desvio Padrão'])}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                        {formatNumber(stat['Coef. Variação (%)'])}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                        {formatNumber(stat['Percentil 25%'])}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                        {formatNumber(stat['Percentil 50%'])}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                        {formatNumber(stat['Percentil 75%'])}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
            </div>
        );
    };

    const StatisticsCharts = ({ statistics, title, type }) => {
        if (!statistics || statistics.length === 0) {
            return (
                <div className="bg-white rounded-lg p-6 mb-6" style={{ outline: 'none', border: 'none' }}>
                    <h3 className="text-lg font-medium text-gray-900 mb-4">{title}</h3>
                    <div className="text-center text-gray-500 py-12">
                        <HiCollection className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                        <p>Nenhum dado disponível</p>
                    </div>
                </div>
            );
        }

        // Preparar dados para os gráficos
        const chartData = statistics.map(stat => ({
            name: stat.Coluna.replace('TRL ', '').replace('CRL ', ''), // Nome curto para eixo X
            fullName: stat.Coluna,
            contagem: stat.Contagem,
            media: stat.Média,
            desvio: stat['Desvio Padrão'],
            coefVar: stat['Coef. Variação (%)']
        }));

        return (
            <>
                {/* Gráfico 1: Contagem */}
                <div className="bg-white rounded-lg p-6 mb-6" style={{ outline: 'none', border: 'none' }}>
                    <h3 className="text-lg font-medium text-gray-900 mb-4">
                        Histograma das Contagens ({type}s)
                    </h3>
                    <div style={{ position: 'relative', outline: 'none', border: 'none' }} tabIndex="-1" onMouseDown={(e) => e.preventDefault()}>
                        <ResponsiveContainer width="100%" height={500}>
                                <BarChart data={chartData} margin={{ top: 5, right: 20, left: 20, bottom: 60 }}>
                                    <CartesianGrid strokeDasharray="3 3" />
                                    <XAxis
                                        dataKey="name"
                                        angle={-45}
                                        textAnchor="end"
                                        height={80}
                                        tick={{ fontSize: 12 }}
                                        label={{ value: 'Transição', position: 'insideBottom'}}
                                    />
                                    <YAxis label={{ value: 'Contagem (projetos)', angle: -90, dx: -20, position: 'insideLeft' }} />
                                    <Tooltip
                                        content={({ active, payload }) => {
                                            if (active && payload && payload.length) {
                                                return (
                                                    <div className="bg-white p-3 border border-gray-300 rounded shadow">
                                                        <p className="text-sm font-medium">{payload[0].payload.fullName}</p>
                                                        <p className="text-sm text-blue-600">Contagem: {payload[0].value}</p>
                                                    </div>
                                                );
                                            }
                                            return null;
                                        }}
                                    />
                                    <Bar dataKey="contagem" fill="#3b82f6" cursor="default" activeBar={false} isAnimationActive={false} />
                                </BarChart>
                        </ResponsiveContainer>
                    </div>
                </div>

                {/* Gráfico 2: Média */}
                <div className="bg-white rounded-lg p-6 mb-6" style={{ outline: 'none', border: 'none' }}>
                    <h3 className="text-lg font-medium text-gray-900 mb-4">
                        Histograma de média de tempo de transição ({type})
                    </h3>
                    <div style={{ position: 'relative', outline: 'none', border: 'none' }} tabIndex="-1" onMouseDown={(e) => e.preventDefault()}>
                        <ResponsiveContainer width="100%" height={500}>
                                <BarChart data={chartData} margin={{ top: 5, right: 20, left: 20, bottom: 60 }}>
                                    <CartesianGrid strokeDasharray="3 3" />
                                    <XAxis
                                        dataKey="name"
                                        angle={-45}
                                        textAnchor="end"
                                        height={80}
                                        tick={{ fontSize: 12 }}
                                        label={{ value: 'Transição', position: 'insideBottom'}}
                                    />
                                    <YAxis label={{ value: 'Média (dias)', angle: -90, dx: -20, position: 'insideLeft' }} />
                                    <Tooltip
                                        content={({ active, payload }) => {
                                            if (active && payload && payload.length) {
                                                return (
                                                    <div className="bg-white p-3 border border-gray-300 rounded shadow">
                                                        <p className="text-sm font-medium">{payload[0].payload.fullName}</p>
                                                        <p className="text-sm text-blue-600">Média: {payload[0].value?.toFixed(2)} dias</p>
                                                    </div>
                                                );
                                            }
                                            return null;
                                        }}
                                    />
                                    <Bar dataKey="media" fill="#3b82f6" cursor="default" activeBar={false} isAnimationActive={false} />
                                </BarChart>
                        </ResponsiveContainer>
                    </div>
                </div>

                {/* Gráfico 3: Desvio Padrão */}
                <div className="bg-white rounded-lg p-6 mb-6" style={{ outline: 'none', border: 'none' }}>
                    <h3 className="text-lg font-medium text-gray-900 mb-4">
                        Histograma dos Desvios Padrão ({type}s)
                    </h3>
                    <div style={{ position: 'relative', outline: 'none', border: 'none' }} tabIndex="-1" onMouseDown={(e) => e.preventDefault()}>
                        <ResponsiveContainer width="100%" height={500}>
                                <BarChart data={chartData} margin={{ top: 5, right: 20, left: 20, bottom: 60 }}>
                                    <CartesianGrid strokeDasharray="3 3" />
                                    <XAxis
                                        dataKey="name"
                                        angle={-45}
                                        textAnchor="end"
                                        height={80}
                                        tick={{ fontSize: 12 }}
                                        label={{ value: 'Transição', position: 'insideBottom'}}
                                    />
                                    <YAxis label={{ value: 'Desvio Padrão (dias)', angle: -90, dx: -20, position: 'insideLeft' }} />
                                    <Tooltip
                                        content={({ active, payload }) => {
                                            if (active && payload && payload.length) {
                                                return (
                                                    <div className="bg-white p-3 border border-gray-300 rounded shadow">
                                                        <p className="text-sm font-medium">{payload[0].payload.fullName}</p>
                                                        <p className="text-sm text-blue-600">Desvio Padrão: {payload[0].value?.toFixed(2)} dias</p>
                                                    </div>
                                                );
                                            }
                                            return null;
                                        }}
                                    />
                                    <Bar dataKey="desvio" fill="#3b82f6" cursor="default" activeBar={false} isAnimationActive={false} />
                                </BarChart>
                        </ResponsiveContainer>
                    </div>
                </div>

                {/* Gráfico 4: Coeficiente de Variação */}
                <div className="bg-white rounded-lg p-6 mb-6" style={{ outline: 'none', border: 'none' }}>
                    <h3 className="text-lg font-medium text-gray-900 mb-4">
                        Histograma dos Coef. Variação (%) ({type}s)
                    </h3>
                    <div style={{ position: 'relative', outline: 'none', border: 'none' }} tabIndex="-1" onMouseDown={(e) => e.preventDefault()}>
                        <ResponsiveContainer width="100%" height={500}>
                                <BarChart data={chartData} margin={{ top: 5, right: 20, left: 20, bottom: 60 }}>
                                    <CartesianGrid strokeDasharray="3 3" />
                                    <XAxis
                                        dataKey="name"
                                        angle={-45}
                                        textAnchor="end"
                                        height={80}
                                        tick={{ fontSize: 12 }}
                                        label={{ value: 'Transição', position: 'insideBottom'}}
                                    />
                                    <YAxis label={{ value: 'Coef. Variação (%)', angle: -90, dx: -20, position: 'insideLeft' }} />
                                    <Tooltip
                                        content={({ active, payload }) => {
                                            if (active && payload && payload.length) {
                                                return (
                                                    <div className="bg-white p-3 border border-gray-300 rounded shadow">
                                                        <p className="text-sm font-medium">{payload[0].payload.fullName}</p>
                                                        <p className="text-sm text-blue-600">Coef. Variação: {payload[0].value?.toFixed(2)}%</p>
                                                    </div>
                                                );
                                            }
                                            return null;
                                        }}
                                    />
                                    <Bar dataKey="coefVar" fill="#3b82f6" cursor="default" activeBar={false} isAnimationActive={false} />
                                </BarChart>
                        </ResponsiveContainer>
                    </div>
                </div>
            </>
        );
    };

    return (
        <div className="p-6 h-full overflow-auto custom-scrollbar">
            {/* Select dropdown */}
            <div className="mb-6">
                <div className="relative inline-block">
                    <select
                        value={viewMode}
                        onChange={(e) => setViewMode(e.target.value)}
                        className="appearance-none bg-white border border-gray-300 rounded px-4 py-2 pr-8 text-sm text-gray-700 focus:border-gray-400 min-w-40"
                    >
                        <option value="TABELA">TABELA</option>
                        <option value="GRAFICOS">GRÁFICOS</option>
                    </select>
                    <HiChevronDown className="absolute right-2 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                </div>
            </div>

            {viewMode === 'TABELA' ? (
                <>
                    {/* Tabela TRL */}
                    <div className="mb-8">
                        <h2 className="text-2xl font-bold text-gray-900 mb-6 border-b-2 border-blue-500 pb-2">
                            Estatística TRL
                        </h2>
                        <StatisticsTable statistics={trlStatistics} title="Estatísticas Descritivas - Transições TRL" />
                    </div>

                    {/* Tabela CRL */}
                    <div className="mb-8">
                        <h2 className="text-2xl font-bold text-gray-900 mb-6 border-b-2 border-green-500 pb-2">
                            Estatística CRL
                        </h2>
                        <StatisticsTable statistics={crlStatistics} title="Estatísticas Descritivas - Transições CRL" />
                    </div>
                </>
            ) : (
                <>
                    {/* Estatísticas TRL */}
                    <div className="mb-8">
                        <h2 className="text-2xl font-bold text-gray-900 mb-6 border-b-2 border-blue-500 pb-2">
                            Estatística TRL
                        </h2>
                        <StatisticsCharts statistics={trlStatistics} title="Gráficos Estatísticos - Transições TRL" type="TRL" />
                    </div>

                    {/* Estatísticas CRL */}
                    <div className="mb-8">
                        <h2 className="text-2xl font-bold text-gray-900 mb-6 border-b-2 border-green-500 pb-2">
                            Estatística CRL
                        </h2>
                        <StatisticsCharts statistics={crlStatistics} title="Gráficos Estatísticos - Transições CRL" type="CRL" />
                    </div>
                </>
            )}
        </div>
    );
};

export default StatisticsTab;
